// @flow

import type {Route} from '@routes';
import type {LocaleKeys} from '@utils/types/locale';

import React, {memo, useEffect, useMemo} from 'react';
import {FormattedMessage, IntlProvider} from 'react-intl';
import {connect, Provider} from 'react-redux';
import {withRouter} from 'react-router';
import {renderRoutes} from 'react-router-config';
import {compose} from 'redux';
import Validators from 'redux-form-validators';
import {DesignSystemProvider} from '@xsolla/design-system-core';
import {ModalProvider} from '@xsolla/design-system-display';
import {publisherV2, publisherV2Dark} from '@xsolla/design-system-themes';
import {Loader} from '@xsolla/uikit';
import MerchantNotSetError from '@components/merchant-not-set-error';
import {
  fetchPaymentMethodsWithInstances,
  fetchProjects
} from '@reducers/disputes/actions/dictionary';
import {fetchDisputeFeatures} from '@reducers/disputes/actions/features';
import {fetchFeatures} from '@reducers/features';
import {normalizeMerchantId} from '@utils/merchant';
import fetchRouteData from './fetchRouteData';
import {getTranslationsData} from './utils/translations';

type Props = {
  +token: string,
  +merchant_id: ?number,
  +project_id: number,
  +locale: LocaleKeys,
  +store: Array<Route>,
  +routes: Array<Route>
};

type BaseProps = {
  +history: any,
  +location: any,
  +routes: any,
  +merchant_id: ?number,
  +project_id: number
};

type AllProps = BaseProps & {
  +projectDetails: any,
  +isFeaturesFetched: boolean,
  +dispatch: any
};

const customTheme = {
  default: publisherV2,
  publisherV2Dark: publisherV2Dark
};

// $FlowFixMe
const App = memo((props: Props) => {
  const {locale, store, routes, merchant_id, project_id} = props;

  // $FlowFixMe - `React.Element` [1] is incompatible with string [2] in the return value.
  Validators.formatMessage = msg => <FormattedMessage {...({props} = msg || msg)} />;

  return (
    <IntlProvider locale={locale} messages={getTranslationsData(locale)}>
      <DesignSystemProvider themes={customTheme} defaultMode="default">
        <Provider store={store}>
          <ModalProvider>
            <FetchRoutesData routes={routes} project_id={project_id} merchant_id={merchant_id} />
          </ModalProvider>
        </Provider>
      </DesignSystemProvider>
    </IntlProvider>
  );
});

const mapStateToProps = state => ({
  isFeaturesFetched: !!state.features?.features?.length
});

const enhance = compose(connect(mapStateToProps), withRouter);

const FetchRoutesData = enhance((props: AllProps) => {
  const {isFeaturesFetched, merchant_id, routes, dispatch, history, location} = props;

  // Never issue requests against an unknown merchant (`/publisher/null/...`).
  const merchantID = normalizeMerchantId(merchant_id);

  useMemo(() => {
    if (!merchantID) {
      return;
    }
    dispatch(fetchDisputeFeatures(merchantID));
  }, [merchantID]);

  useEffect(() => {
    if (!merchantID) {
      return;
    }
    if (!isFeaturesFetched) {
      dispatch(fetchFeatures(merchantID));
    }
    fetchRouteData({history, location, route: routes[0], dispatch});
  }, [merchantID]);

  useEffect(() => {
    if (!merchantID) {
      return;
    }
    dispatch(fetchFeatures(merchantID));
    dispatch(fetchPaymentMethodsWithInstances(merchantID));
    dispatch(fetchProjects(merchantID));
  }, [merchantID]);

  const pathname = location.pathname;
  useEffect(() => {
    fetchRouteData({history, location, route: routes[0], dispatch});
  }, [pathname]);

  if (!merchantID) {
    return <MerchantNotSetError />;
  }

  if (!isFeaturesFetched) {
    return <Loader fullscreen={true} size="xlg" color="blue" />;
  }
  return <>{renderRoutes(routes)}</>;
});

export default App;
